import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, KeyRound, Loader2, Mail } from "lucide-react";
import PartnerAuthShell from "@/components/partners/PartnerAuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { getCsrfToken } from "@/integrations/php/client";

const PartnerPasswordReset = () => {
  const [token] = useState(() => {
    const url = new URL(window.location.href);
    const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
    const candidate = new URLSearchParams(hash).get("token") || url.searchParams.get("token") || "";
    if (/^[a-f0-9]{64}$/.test(candidate)) {
      url.searchParams.delete("token");
      url.hash = "";
      window.history.replaceState(null, "", `${url.pathname}${url.search}`);
      return candidate;
    }
    return "";
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (token && (password.length < 12 || password !== confirmPassword)) {
      toast({ title: "Ελέγξτε τον κωδικό", description: "Χρειάζονται τουλάχιστον 12 χαρακτήρες και ίδια επιβεβαίωση.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(token ? "/api/partner-password-reset.php" : "/api/partner-password-reset-request.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify(token ? { token, password } : { email }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error?.message || "Η ενέργεια δεν ολοκληρώθηκε.");
      setComplete(true);
    } catch (error) {
      toast({ title: "Η ενέργεια απέτυχε", description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PartnerAuthShell>
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
        <Link to="/partner/login" className="mb-6 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-slate-600 hover:text-sky-700"><ArrowLeft className="h-4 w-4" /> Πίσω στη σύνδεση</Link>
        {complete ? (
          <div className="text-center"><div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-7 w-7" /></div><h1 className="mt-4 text-2xl font-bold text-slate-950">{token ? "Ο κωδικός ορίστηκε" : "Ελέγξτε το email σας"}</h1><p className="mt-2 text-sm leading-6 text-slate-600">{token ? "Μπορείτε τώρα να συνδεθείτε με τον νέο κωδικό." : "Αν υπάρχει ενεργός λογαριασμός με αυτό το email, στείλαμε σύνδεσμο που ισχύει για 30 λεπτά."}</p><Button asChild className="mt-6 h-12 w-full rounded-xl bg-sky-600 hover:bg-sky-700"><Link to="/partner/login">Μετάβαση στη σύνδεση</Link></Button></div>
        ) : (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-sky-700">{token ? <KeyRound className="h-6 w-6" /> : <Mail className="h-6 w-6" />}</div>
            <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-950">{token ? "Ορισμός νέου κωδικού" : "Δημιουργία ή επαναφορά"}</h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">{token ? "Ο σύνδεσμος χρησιμοποιείται μία φορά. Επιλέξτε έναν κωδικό με τουλάχιστον 12 χαρακτήρες." : "Συμπληρώστε το email σας και θα λάβετε έναν ασφαλή σύνδεσμο."}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              {token ? <><label className="block space-y-2"><Label htmlFor="new-password">Νέος κωδικός</Label><Input id="new-password" type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={12} required className="h-12 rounded-xl bg-slate-50 text-base sm:text-sm" /></label><label className="block space-y-2"><Label htmlFor="confirm-password">Επιβεβαίωση κωδικού</Label><Input id="confirm-password" type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={12} required className="h-12 rounded-xl bg-slate-50 text-base sm:text-sm" /></label></> : <label className="block space-y-2"><Label htmlFor="reset-email">Email συνεργάτη</Label><Input id="reset-email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(event) => setEmail(event.target.value)} required className="h-12 rounded-xl bg-slate-50 text-base sm:text-sm" placeholder="name@example.gr" /></label>}
              <Button type="submit" disabled={loading} className="h-12 w-full rounded-xl bg-sky-600 text-base font-bold hover:bg-sky-700">{loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}{token ? "Αποθήκευση κωδικού" : "Αποστολή συνδέσμου"}</Button>
            </form>
          </>
        )}
      </div>
    </PartnerAuthShell>
  );
};

export default PartnerPasswordReset;
